import { onCommand, onTransportDown, whenProbeSettles, whenStreamTrusted } from '../servers/commandStream'
import { notifySignedOut } from '../auth/sessionSignal'
// Type-only: erased at build, so api.ts importing this module back is no cycle at run time.
import { UPLOAD_CHUNK, b64, type TranscriptBlock } from './api'

/**
 * How a request reaches the machine now that the browser cannot.
 *
 * Every screen this app has was once an ordinary fetch to the machine itself. It
 * opens no port here, so the request travels as a command: down the account's
 * socket to the router, down the link the machine already holds open, served on
 * that machine's own loopback socket through its own auth and vault gate, and the
 * answer comes back the same way. Two hops, both pushed, no timer anywhere.
 *
 * WHICH MACHINE is a real question, and it has no global answer. v1 had one edge
 * that mounted remotes, so `host` was a field on a row; here every paired machine
 * holds its own link, and an agent's requests belong to the machine that agent
 * lives on. The target is therefore something the UI sets as the user moves
 * around, not a setting.
 */

const KEY = 'av_relay_device'

let target: string | null = readStored()

function readStored(): string | null {
  try {
    return localStorage.getItem(KEY)
  } catch {
    // Private mode, or a shell with no storage. Not having a remembered machine is
    // a worse experience, not a broken one.
    return null
  }
}

/** The machine every relayed request goes to until told otherwise. */
export function relayTarget(): string | null {
  return target
}

const listeners = new Set<(id: string | null) => void>()

/**
 * Be told when the relay gets a machine to point at. Returns an unsubscribe.
 *
 * This existed once and I deleted it as an unused export — correctly, by the rule,
 * and the thing that needed it had not been written yet. The terminal opens its
 * stream the moment it mounts, while `chooseRelayTarget` is still asking the router
 * which machines the account has: a race the terminal lost on every fresh load,
 * reporting a disconnect for a machine nobody had chosen yet.
 */
export function onRelayTargetChange(cb: (id: string | null) => void): () => void {
  listeners.add(cb)
  return () => listeners.delete(cb)
}

export function setRelayTarget(deviceId: string | null): void {
  if (target === deviceId) return
  target = deviceId
  try {
    if (deviceId) localStorage.setItem(KEY, deviceId)
    else localStorage.removeItem(KEY)
  } catch {
    // See readStored: remembering is the optional half.
  }
  for (const cb of [...listeners]) cb(deviceId)
}

/** No machine has been chosen yet — the screens above must ask, not guess. */
export class NoMachineChosen extends Error {
  constructor() {
    super('no machine chosen')
    this.name = 'NoMachineChosen'
  }
}

/**
 * The machine is not on the link right now.
 *
 * Thrown rather than waited out. The router tells us at submit time whether the
 * command was delivered live; when it was not, the machine is away and the queue
 * is durable — it will run whenever that machine comes back, which is the right
 * behaviour for "create an agent" and the wrong one for a screen someone is
 * looking at. Waiting would spend an unbounded amount of a person's attention to
 * arrive at the same sentence.
 */
export class MachineAway extends Error {
  constructor(readonly deviceId: string) {
    super('that machine is not connected')
    this.name = 'MachineAway'
  }
}

/**
 * How long a live command may go unanswered before we stop believing in it.
 *
 * Not a poll and not a heartbeat: nothing is sent while this runs. It exists
 * because an open socket can still miss a frame, and "the stream is up" must never
 * silently become "the answer will come" — a screen that waits for ever is
 * indistinguishable from one that is broken. Matched to the machine's own 30s
 * ceiling on serving a request, so this fires only when that one did not.
 */
const ANSWER_TIMEOUT_MS = 35_000

/*
 * READS GET A SHORT DEADLINE AND A RESUBMIT; only mutations wait the full ceiling.
 *
 * The answer to a relayed request travels edge → link → sibling BFF → events socket,
 * and for http.request NOTHING IS STORED on the way (deliberately — a read should not
 * pay a write). So the stream is the only channel, and a lost frame has no second
 * chance: the waiter sits the full 35s while the screen freezes. The owner measured
 * the consequence — create works, then the rail takes forever, because the post-create
 * fan-out asks every machine and Promise.all waits for the slowest loss.
 *
 * A GET is idempotent, so the honest recovery is to ASK AGAIN: a fresh command, a
 * fresh delivery, a fresh answer — bounded at two attempts of 4s before giving up
 * loudly. On-machine service time is ~300ms, so 4s is generous for everything except
 * a lost frame, which is exactly what it exists to survive. Mutations keep the long
 * wait and are never auto-retried: a create that "did not answer" may well have run.
 */
const READ_ANSWER_TIMEOUT_MS = 4_000
const READ_ATTEMPTS = 2

/**
 * A DEADLINE ON THE SUBMIT ITSELF — the third address of a bug already fixed twice.
 *
 * Owner, 2026-09-21: 現在上傳圖片的時候,有一定的機率還是會卡住. Everything BELOW this
 * submit was already bounded (answerFor above, whenStreamTrusted in commandStream),
 * and the submit was not: a bare `fetch` does not reject on a stalled connection, it
 * never settles. An await that never settles cannot be caught, so the composer's own
 * `finally` never ran — `sending` stayed true and the send button stayed dead, with
 * the photo still in the tray. That is 卡住: not a failure, an absence.
 *
 * WHY PHOTOS AND NOT TEXT. A note file crosses in 512 KiB slices, each carried as
 * base64 in the BODY OF THIS POST — the largest and longest-on-the-wire request the
 * app makes. A 434 KB image is three passes through here; the 100 MB ceiling is 202.
 * Each pass is an independent chance to stall, which is exactly 「有一定的機率」.
 *
 * The same defect was fixed on the project-chat S3 POST and in chatv1's transport,
 * and `chat/uploadStall.test.ts` pins the signal on exactly those two files — its own
 * comment says "the two are one defect with two addresses". There was a third, and it
 * was the one every agent-session upload uses.
 *
 * ── THE NUMBERS, EACH TAKEN FROM ONE ALREADY IN THIS REPO ────────────────────
 *
 * Scaled by body size rather than fixed, with the floor and the per-MB allowance
 * s3UploadTimeoutMs already uses, because this deadline exists to bound a stall and
 * must never abort a big body that is moving perfectly well on a slow uplink — the
 * same bug wearing the opposite sign. Measured: the largest legitimate body here is
 * ~683 KiB (a 512 KiB slice is exactly 4/3 in base64), so an upload chunk gets about
 * 43s while an ordinary few-hundred-byte request sits on the 30s floor.
 *
 * ── AbortController, NOT AbortSignal.timeout ─────────────────────────────────
 *
 * `AbortSignal.timeout` is Safari 16, and ios/ still declares
 * IPHONEOS_DEPLOYMENT_TARGET 15.0. Two files already use it (chatv1/transport.ts,
 * chat/projectDynamo.ts) and would already throw on such a device — but those break
 * chat, and THIS function carries every request in the app. Turning an intermittent
 * upload stall into a total outage on an older phone is not a trade worth making for
 * three fewer lines, so the deadline is built the way that works everywhere.
 */
const SUBMIT_FLOOR_MS = 30_000
const SUBMIT_PER_MB_MS = 20_000

export function submitTimeoutMs(bodyBytes: number): number {
  /*
   * PROPORTIONAL, where s3UploadTimeoutMs rounds UP to the next megabyte — the one
   * number here that deviates from it, and deliberately. There, `bytes` is a whole
   * file and the smallest real one still deserves the allowance. Here almost every
   * body is a few hundred bytes of JSON, and `Math.ceil` would hand each of them the
   * full per-MB grant: the "floor" would be a value nothing ever received, and every
   * read in the app would carry a 50s ceiling to cover a 683 KiB upload chunk.
   */
  const mb = Math.max(0, bodyBytes) / (1024 * 1024)
  return SUBMIT_FLOOR_MS + Math.round(mb * SUBMIT_PER_MB_MS)
}

/**
 * The submit's own words. It must contain "did not answer" so `relayRead` keeps
 * retrying a GET that starved here exactly as it retries one that starved waiting —
 * from the caller's side those are the same event, and the resubmit is what recovers
 * both. It says WHERE it gave up so the two are still distinguishable in a report.
 */
const SUBMIT_TIMEOUT_MESSAGE = 'the machine did not answer — the request stalled on its way out'

export type RelayAnswer = { status: number; body: unknown }

/**
 * Headers that may travel, and no others.
 *
 * The first version of this relay carried none, and the one that mattered was
 * X-CSRF-Token: /security/* mutations require it, so they would have refused —
 * correctly, and for a reason that looked like a server fault. A test written
 * against the old transport is what said so.
 *
 * An allowlist rather than a passthrough, because "whatever the caller set" on a
 * general relay lets a screen reach the machine with headers nobody reviewed. Two
 * entries is the whole need; the third should be argued for.
 */
const SENDABLE_HEADERS = ['x-csrf-token', 'content-type']

function allowedHeaders(init?: HeadersInit): Record<string, string> | undefined {
  if (!init) return undefined
  const out: Record<string, string> = {}
  new Headers(init).forEach((value, key) => {
    if (SENDABLE_HEADERS.includes(key.toLowerCase())) out[key] = value
  })
  return Object.keys(out).length ? out : undefined
}

export async function relayRequest(
  method: string,
  path: string,
  body: unknown,
  headers?: HeadersInit,
  deviceId: string | null = target,
): Promise<RelayAnswer> {
  if (!deviceId) throw new NoMachineChosen()

  /*
   * EARS FIRST. The answer to this request travels back on the account events
   * socket and is stored nowhere on the way — so submitting before that socket
   * is open loses the answer unconditionally, and the waiter times out into
   * "the machine did not answer" on a machine that answered fine (the startup
   * race the owner and Rachel both hit; a refresh "fixed" it because by then
   * the socket was up). Bounded: if the socket cannot open in time we submit
   * anyway and earn the same honest error as before.
   *
   * LIVE EARS, since 2026-09-05: open is not enough. A half-open socket reads OPEN
   * and delivers nothing, and a MUTATION submitted over it cannot be retried — it
   * may well have run — so the waiter sat the full 35 s and the composer looked
   * stuck until a reload. When the socket has been silent a while, this probes it
   * first (whenStreamTrusted): one round trip on a live pipe, a reconnect on a
   * dead one, and the submit then rides a socket that has just proven it can
   * carry the answer back.
   */
  await whenStreamTrusted()

  const payload = JSON.stringify({
    method,
    path,
    body: body ?? null,
    headers: allowedHeaders(headers),
  })

  /*
   * THE DEADLINE COVERS THE BODY READS TOO, and that is why it is one controller
   * around the whole block rather than a timeout on the fetch call alone. `fetch`
   * settles when the HEADERS arrive; `res.json()` below reads the STREAM, and a
   * response whose body stalls after its headers never settles either. Aborting the
   * fetch errors that stream, so one signal closes all three awaits — a
   * `Promise.race` around the fetch would have left the two json() calls open and
   * would not have cancelled the request either.
   */
  const ac = new AbortController()
  let timedOut = false
  const deadline = setTimeout(() => {
    timedOut = true
    ac.abort()
  }, submitTimeoutMs(payload.length))

  let submitted: { command_id?: string; delivered_live?: boolean }
  try {
    const res = await fetch(`/account/devices/${encodeURIComponent(deviceId)}/request`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      signal: ac.signal,
    })
    if (res.status === 401) {
      notifySignedOut()
      throw new Error('signed out')
    }
    if (!res.ok) {
      let detail = res.statusText
      try {
        const parsed = (await res.json()) as { error?: unknown; detail?: unknown }
        detail = String(parsed.detail ?? parsed.error ?? detail)
      } catch {
        // Non-JSON refusal from the router; the status line is what we have.
      }
      throw new Error(`the router refused this request: ${detail}`)
    }
    submitted = (await res.json()) as { command_id?: string; delivered_live?: boolean }
  } catch (e) {
    /*
     * OUR deadline, not the caller's cancel and not a network error: `timedOut` is
     * the only thing that can tell them apart, because all three surface as the same
     * AbortError. Translated into the house sentence so a person reads why, and so
     * relayRead's resubmit still recognises it.
     */
    if (timedOut) throw new Error(SUBMIT_TIMEOUT_MESSAGE)
    throw e
  } finally {
    // Cleared on EVERY path. Left running, each request would hold a 30s timer and
    // an abort that fires into nothing — harmless once, and this function is the
    // busiest in the app.
    clearTimeout(deadline)
  }
  const commandId = submitted.command_id
  if (!commandId) throw new Error('the router queued nothing')
  if (submitted.delivered_live === false) throw new MachineAway(deviceId)

  return await answerFor(commandId, method === 'GET' ? READ_ANSWER_TIMEOUT_MS : ANSWER_TIMEOUT_MS)
}

/**
 * A GET, resubmitted on a lost answer. Split from relayRequest so mutations cannot
 * drift into being retried by a refactor that meant well — the retry loop simply has
 * no path that carries a non-GET.
 */
export async function relayRead(
  path: string,
  deviceId: string | null = target,
): Promise<RelayAnswer> {
  let lastErr: unknown
  for (let attempt = 0; attempt < READ_ATTEMPTS; attempt++) {
    try {
      return await relayRequest('GET', path, null, undefined, deviceId)
    } catch (e) {
      lastErr = e
      // MachineAway and refusals are answers, not losses — asking again would get
      // the same sentence and spend the user's wait to do it.
      if (!(e instanceof Error) || !/did not answer/.test(e.message)) throw e
      /*
       * THE TIMEOUT ITSELF IS THE EVIDENCE. A read that starved while the socket
       * claimed OPEN is exactly how a half-open mobile socket presents (owner,
       * 2026-08-25: the stage stuck on "Waiting for tbc-m1 to answer" with every
       * machine answering server-side; a reload — a fresh socket — fixed it
       * instantly). The visibility-wired probes never fire while someone stares
       * at the stuck screen, so the starving read brings the trigger: probe,
       * wait for the verdict, and let the retry ride a live pipe instead of the
       * same corpse. On a genuinely slow machine the probe answers at once and
       * this await costs a round trip, not a deadline.
       */
      if (attempt + 1 < READ_ATTEMPTS) await whenProbeSettles()
    }
  }
  throw lastErr
}

/**
 * Wait for exactly this command's answer.
 *
 * The waiter is registered against the command id, so two requests in flight
 * cannot be confused for one another — the failure that only appears once a real
 * screen issues more than one at a time, which is every screen.
 */
function answerFor(commandId: string, timeoutMs: number = ANSWER_TIMEOUT_MS): Promise<RelayAnswer> {
  return new Promise<RelayAnswer>((resolve, reject) => {
    let done = false
    const finish = (fn: () => void) => {
      if (done) return
      done = true
      clearTimeout(timer)
      off()
      offDown()
      fn()
    }

    const timer = setTimeout(
      () => finish(() => reject(new Error('the machine did not answer'))),
      timeoutMs,
    )

    /*
     * THE ANSWER TRAVELS DOWN A SOCKET THAT CAN DIE UNDER IT.
     *
     * The reply comes back on the account events socket and is stored nowhere on
     * the way, so if THIS screen loses its socket the answer lands in a process
     * that has no queue to put it in and is dropped. Waiting out the deadline
     * after that is waiting for something that already did not happen.
     *
     * The owner found this by noticing the shape of it: aowl and tbc-m1 "flapped
     * together, every time". Two machines cannot fail in the same instant — but
     * the ONE socket their answers share can, and the screen asks every machine
     * at once. Simultaneous failures across independent machines are the
     * fingerprint of a single shared thing, and the shared thing was here.
     * Server-side there was no link drop at all in that window.
     *
     * So: say so at once. The wording carries "did not answer" deliberately —
     * `relayRead` retries on exactly that phrase, and `relayRequest` waits for
     * the socket to be back before submitting, so a read recovers as soon as the
     * tab does instead of four seconds later. It never claims the command did
     * not run, because on a mutation it may well have.
     */
    const offDown = onTransportDown(() =>
      finish(() =>
        reject(new Error(
          'the machine did not answer: the connection to this screen dropped ' +
          'before the reply arrived',
        )),
      ),
    )

    const off = onCommand(commandId, (state) => {
      if (state.state === 'queued' || state.state === 'running') return
      if (state.state === 'done') {
        const result = state.result as { status?: unknown; body?: unknown } | null
        if (!result || typeof result.status !== 'number') {
          finish(() => reject(new Error('the machine answered in a shape this cannot read')))
          return
        }
        finish(() => resolve({ status: result.status as number, body: result.body }))
        return
      }
      // failed, or `unknown` — taken but never reported. Both are "no answer", and
      // the difference matters to a person deciding whether to try again, so the
      // machine's own words travel rather than a word of ours.
      finish(() => reject(new Error(state.error || `the command ended ${state.state}`)))
    })
  })
}

/**
 * Point the relay at a machine without asking anyone.
 *
 * v1 had no concept of choosing one: the app WAS a machine, and its sidebar
 * grouped agents by mounted host. Making every session start with "which machine"
 * would be a question the product never asked, so this answers it the way a person
 * would — the one that is connected, and the one you used last if it still is.
 *
 * Returns what it settled on, or null when the account has no machine that can
 * answer. Null is a real state (nothing paired yet) and the screens above must be
 * able to tell it apart from "not asked yet", which is why it is returned rather
 * than swallowed.
 */
export async function chooseRelayTarget(): Promise<string | null> {
  let devices: { device_id?: string; live?: boolean; last_seen?: number }[] = []
  try {
    const res = await fetch('/account/devices', { credentials: 'include', cache: 'no-store' })
    if (res.status === 401) {
      notifySignedOut()
      return null
    }
    if (!res.ok) return relayTarget()
    const body = (await res.json()) as { devices?: typeof devices }
    devices = Array.isArray(body.devices) ? body.devices : []
  } catch {
    // Offline, or the router is down. Keeping the remembered machine is better
    // than clearing it: the next request will fail with its own words either way,
    // and forgetting would also lose the choice across a blip.
    return relayTarget()
  }

  const ids = new Set(devices.map((d) => String(d.device_id ?? '')))
  const current = relayTarget()
  // `live === true` means an instance is holding that machine's connection right
  // now. `null` means a sibling instance might be — not a no, so it stays eligible,
  // just behind anything we can see for ourselves.
  const live = devices.filter((d) => d.live === true).map((d) => String(d.device_id))
  const maybe = devices.filter((d) => d.live !== false).map((d) => String(d.device_id))

  /*
   * A PAIRED TARGET IS KEPT EVEN WHILE IT IS NOT LIVE.
   *
   * This used to move the target the moment the current machine read as not
   * live — and a machine reads as not live for the few seconds its edge takes
   * to restart on every auto-update. Moving it then is not a fallback, it is a
   * silent re-addressing of every relayed request: the open conversation's
   * transcript now 404s on a machine that never had it, and a message sent to
   * an agent by NAME lands on whichever same-named agent the new machine holds
   * (2026-08-24: update-agent-* exists on four machines; the wrong one answered
   * `delivered: true`, truthfully, for the wrong agent). The request that fails
   * against a briefly-down machine at least fails in its own words.
   *
   * So the target is only re-chosen when there is none, or when the one we hold
   * is no longer paired at all.
   */
  if (current && ids.has(current)) return current
  const next = live[0] ?? maybe[0] ?? null
  setRelayTarget(next)
  return next
}

/* ── paired machines (BFF /account routes, session-cookie auth) ──────────────
   Unpair is initiated on the ACCOUNT side by ruling: a command a machine can
   run on itself is a command anything on that machine can run. The device is
   outbound-only, so it learns at its next connect (403 device_revoked); until
   it has been told, a pending unpair can still be cancelled. */

/**
 * One agent as the CLOUD sees it, under the machine it is attached to.
 *
 * This rode along untyped for a while — `blockedAgents.ts` reached in with a cast
 * because it needed one field. It has three consumers now, so it is declared.
 *
 * `fork_local` / `fork_cross` are TRI-STATE and the third state is load-bearing:
 * `null` means the machine has not told us, which is not the same as "cannot be
 * forked". The rule lives on the edge (`forkPlan`), and a client that re-derives it
 * from `cli` drifts the moment a CLI changes tier — the drift being a button offered
 * on something it can never work on, or worse, a working button hidden. So: act on
 * `false`, never on absence.
 */
export type AccountAgent = {
  /** The agent's own hash — how a row here is matched to an agent in the rail. */
  channel_hash?: string
  session?: string
  label?: string
  /** Why it cannot be reached right now: 'at_prompt' | 'typing' | … See blockedAgents. */
  blocked?: string | null
  /** Which CLI it runs, for display. The fork rule is the flags, not this. */
  cli?: string | null
  /** Can it be forked on its own machine? null = the machine never said. */
  fork_local?: boolean | null
  /** Can it be forked onto ANOTHER machine? null = the machine never said. */
  fork_cross?: boolean | null
}

export type AccountDevice = {
  device_id: string
  /** The agents attached on this machine, as the cloud knows them. */
  agents?: AccountAgent[]
  /**
   * Why this machine is or is not on the current build, as IT reports it.
   *
   * ABSENT, never defaulted: a build older than the field says nothing, and the
   * machines most likely to be stuck are exactly the ones too old to speak. avg-main
   * and avg-devenv both refused to fill it in on the way through for that reason —
   * "this version cannot tell us" and "this machine says it is fine" must not land in
   * the same row. Shape and rules live in servers/updateHealth.ts.
   */
  update_health?: unknown
  /** What the machine calls itself — the readable name for the row. */
  hostname?: string
  /** What the ACCOUNT calls the machine — a nickname stored in DynamoDB keyed by
   *  device_id, so one rename shows up on every app end. Never sent by the machine;
   *  set via setDeviceNickname.
   *
   *  DO NOT SPELL THE DISPLAY RULE AGAIN — call `deviceDisplayName` (transport/deviceName.ts,
   *  a leaf module so anything can import it without pulling this one's transport in). It
   *  used to describe the rule and invite each caller to implement it, and four of them
   *  did, in four subtly different ways. Two shipped bugs from it. */
  nickname?: string | null
  device_fp?: string
  /**
   * The build that machine's agent is RUNNING, self-reported on its beat — not
   * what sits on its disk (owner, 2026-08-20: put the hash on the machine list,
   * say whether it is out of date, and let me update from there).
   *
   * MISSING is not the same as BEHIND. An edge too old to report it sends
   * nothing, and rendering that as "out of date" would be a guess dressed as a
   * fact — it is shown as unknown instead.
   */
  version?: string
  state?: 'active' | 'pending_unpair' | string
  /** true = a link is up now; false = known away; null = this BFF can't tell
   *  (a sibling instance may hold the link) — read `last_seen` for those. */
  live?: boolean | null
  last_seen?: number
  told_at?: number
  /** A node of a shared home (a Slurm cluster's compute node): the device id of the machine that owns
   *  that home. Null or absent on every other machine — and from a BFF too old to say. */
  sibling_of?: string | null
}



/**
 * A DIRECT call to this origin's BFF (session-cookie auth), NOT a relay to a paired
 * device. Every `/account/*` route the BFF serves itself — devices, friends — goes
 * through here; the api.get/api.post lane relays to an edge instead, which does not
 * have these routes and answers `not_found`.
 */
/**
 * A refusal from the BFF. `message` is exactly what bffJson has always thrown (the
 * body's detail, else its code, else the status), so every existing caller reads the
 * same text; the code, status and body ride along for a caller that has to branch on
 * WHICH refusal it was — the backup sheet tells `vault_exists` from `quota_exceeded`
 * and shows the server's own sentence, which the BFF sends as `message`.
 */
export class BffError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string | null,
    readonly body: Record<string, unknown>,
  ) {
    super(message)
    this.name = 'BffError'
  }
}

async function bffJson<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(path, { credentials: 'include', cache: 'no-store', ...init })
  /*
   * Strict on purpose. The first draft swallowed a JSON parse failure into {},
   * and on a 200 that returns `undefined` fields downstream — the machines
   * list rendered "no machines paired" for an answer that was never a device
   * list at all. A 200 that is not JSON is an ERROR with the first bytes as
   * evidence, never an empty object.
   */
  const text = await r.text()
  let body: T & { error?: string; detail?: string }
  try {
    body = JSON.parse(text) as typeof body
  } catch {
    throw new Error(`${path} answered ${r.status} with non-JSON: ${text.slice(0, 80)}`)
  }
  if (!r.ok) {
    throw new BffError(
      body.detail ?? body.error ?? `${r.status} on ${path}`,
      r.status,
      typeof body.error === 'string' ? body.error : null,
      (body ?? {}) as Record<string, unknown>,
    )
  }
  return body
}

/**
 * GLOBAL MESSAGE SEARCH — POST /account/search (BFF routes_search.py), owner 2026-09-28:
 * the side panel's search button searches every message on every machine, and the
 * Agent Session's search asks the edge instead of the frontend's cache. The BFF asks
 * every machine of the account (or, with device_id + hash, one agent on one machine)
 * and merges the edges' ranked answers; every machine is accounted for in `machines`.
 * A BFF route, so bffJson — `api` would relay it to an edge, which has no such route.
 */
export type SearchBlockKind = 'user' | 'assistant' | 'thinking' | 'tool_use' | 'tool_result'

export type AccountSearchRequest = {
  keywords: string[]
  /** One keyword is enough (default: every keyword must appear). */
  any?: boolean
  kinds?: SearchBlockKind[]
  sort?: 'score' | 'time' | 'relevance'
  /** 1–100, default 50. */
  limit?: number
  half_life_days?: number
  /** Characters of text per result, around the first keyword; 1–20000, default 2000. */
  max_text?: number
  /** One agent: the machine it runs on, and its hash. */
  device_id?: string
  hash?: string
}

export type AccountSearchResult = {
  device_id: string
  /** The machine as the account names it. */
  machine: string
  hash: string
  session: string
  agent_kind: string
  /** The block's place in its conversation — the Clean View's own numbering. */
  index: number
  kind: SearchBlockKind
  ts?: number
  ts_estimated?: boolean
  name?: string
  /**
   * Who sent it, when the message ARRIVED — from another agent, a chat or a project
   * room — rather than being typed by the owner; absent on the owner's own words. A
   * `user` block is either, so this is what tells "you" from someone else. `text` is
   * then the sender's words, without the framing (the edge's inboundOf).
   */
  from?: { kind: 'agent' | 'chat' | 'project'; hash?: string; label?: string; room?: string }
  text: string
  text_length: number
  text_truncated?: boolean
  hits: string[]
  score: number
  relevance: number
  recency: number
}

export type AccountSearchMachine = {
  device_id: string
  machine: string
  status: 'ok' | 'offline' | 'needs_update' | 'not_here' | 'busy' | 'timeout' | 'error'
  detail?: string
  total_matches?: number
  searched?: number
  skipped?: number
}

export type AccountSearchReport = {
  ok: true
  query: AccountSearchRequest
  results: AccountSearchResult[]
  machines: AccountSearchMachine[]
  total_matches: number
  machines_searched: number
  machines_total: number
  elapsed_ms: number
}

export const accountSearch = (req: AccountSearchRequest, signal?: AbortSignal) =>
  bffJson<AccountSearchReport>('/account/search', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(req),
    ...(signal ? { signal } : {}),
  })

/**
 * PUSH NOTIFICATIONS — the opt-in surface, all four BFF routes.
 *
 * Here rather than in src/push for the reason bffJson's own comment already
 * states: `api.post` RELAYS to a paired machine's edge, which has no
 * /account/push and answers `not_found`. That is exactly what happened on the
 * owner's phone on 2026-09-18 — the switch failed with an edge's 404 long after
 * the BFF was rolled out and answering fine, and the giveaway was the word
 * itself: `not_found` is the edge's, `unknown_route` is the BFF's.
 */
export const pushPrefsGet = () =>
  bffJson<{ prefs?: Array<{ device_id: string; channel_hash: string }>; default?: boolean }>(
    '/account/push/prefs',
  )

export const pushPrefSet = (device_id: string, channel_hash: string, enabled: boolean) =>
  bffJson<{ ok: boolean }>('/account/push/prefs', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ device_id, channel_hash, enabled }),
  })

/**
 * Which chat rooms notify this person — one row per room they switched ON.
 *
 * Same lane and same reasoning as the agent prefs above: /account/* is the BFF's,
 * and `api` from transport/http would relay it to a machine that has no such route.
 */
export const roomPushGet = () =>
  bffJson<{ rooms?: Array<{ conv_id: string }>; default?: boolean }>('/account/push/rooms')

export const roomPushSet = (conv_id: string, enabled: boolean) =>
  bffJson<{ ok: boolean }>('/account/push/rooms', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ conv_id, enabled }),
  })

export const pushDeviceRegister = (
  token: string, bundle_id: string, environment: string, platform = 'ios',
) =>
  /*
   * PLATFORM COMES FROM THE SHELL, not from here. There are two now — the phone
   * (Capacitor) and the Mac (a bare WKWebView with a message handler) — and the
   * Mac reads its environment from its own aps entitlement rather than from a
   * bundle name. Hard-coding 'ios' was fine while there was one shell and is a
   * lie the moment there are two.
   */
  bffJson<{ ok: boolean }>('/account/push/devices', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, bundle_id, environment, platform }),
  })

export const pushDeviceUnregister = (token: string) =>
  bffJson<{ ok: boolean; dropped: number }>('/account/push/devices/unregister', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  })

export const getAccountDevices = () =>
  // `email` rides along so an EMPTY list can say WHOSE it is — two Google
  // accounts are in play on this deployment, and "no machines" is a true
  // answer about the wrong account more often than a bug.
  bffJson<{ email?: string; devices: AccountDevice[] }>('/account/devices')

/**
 * Fork an agent's conversation onto ANOTHER machine (avg-devenv, 2026-08-22).
 *
 * A BFF route, NOT a relayed command — and that distinction is load-bearing here, not
 * a style choice. `api.post` travels as a command to one machine and gives up after
 * 35 seconds (ANSWER_TIMEOUT_MS); this call legitimately takes up to 300s, because the
 * BFF is orchestrating BOTH machines behind it. Sent the relay way, a fork that was
 * succeeding would be abandoned at 35s and reported as a failure while it went on to
 * create the agent — the worst shape available: a phantom, plus a lie about it.
 *
 * `fork_id` is minted HERE so the caller can ask about progress before the answer
 * comes back, and so a retry joins the first attempt instead of starting a second one
 * (the endpoint is single-flight on it for 90s).
 *
 * The body is returned whatever the status: the failure CODE is what the UI turns into
 * a sentence (agents/forkAcross.ts), and bffJson's throw would flatten it to a string
 * with `detail` winning over `error` — losing the one field this needs most.
 */
export type ForkAcrossResult = {
  ok?: boolean
  error?: string
  message?: string
  detail?: string
  fork_id?: string
  source_id?: string
  /** Which CLI the forked agent runs. Only `claude` in this version. */
  cli?: string
  target?: { session?: string; cwd?: string; [k: string]: unknown }
}

/**
 * File a report about content or behaviour — owner, 2026-09-15.
 *
 * RETURNS a result rather than throwing, unlike its neighbours. A form needs to put
 * the reason back on the screen the person is looking at; `bffJson` throws with
 * `detail ?? error`, which for this route is the machine code ("empty", "too_long")
 * and not the sentence the server wrote for a human to read. Rather than reshape the
 * shared helper — or the server's body — to suit one form, this reads the answer it
 * was given.
 */
export type ReportResult = { ok: true } | { ok: false; message: string }

export const sendReport = async (input: { text: string; about?: string; appBuild?: string }): Promise<ReportResult> => {
  let r: Response
  try {
    r = await fetch('/account/report', {
      method: 'POST',
      credentials: 'include',
      cache: 'no-store',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        text: input.text,
        ...(input.about ? { about: input.about } : {}),
        ...(input.appBuild ? { app_build: input.appBuild } : {}),
      }),
    })
  } catch {
    // Offline, or the request never left. Say so plainly: a report that silently
    // failed is worse than one that was never written, because the person believes
    // it was sent.
    return { ok: false, message: 'Could not reach the server. Check your connection and try again.' }
  }
  const text = await r.text()
  let body: { ok?: boolean; error?: string; message?: string }
  try {
    body = JSON.parse(text) as typeof body
  } catch {
    return { ok: false, message: `The server answered ${r.status} with something unexpected.` }
  }
  if (r.ok && body.ok) return { ok: true }
  if (r.status === 401 || r.status === 403) return { ok: false, message: 'Please sign in again, then send this report.' }
  return { ok: false, message: body.message ?? body.error ?? `The server answered ${r.status}.` }
}

export const forkAcross = async (input: {
  forkId: string
  sourceDeviceId: string
  sourceSession: string
  targetDeviceId: string
  cwd: string
  newName?: string
}): Promise<ForkAcrossResult> => {
  const r = await fetch('/account/agents/fork-across', {
    method: 'POST',
    credentials: 'include',
    cache: 'no-store',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      fork_id: input.forkId,
      source_device_id: input.sourceDeviceId,
      source_session: input.sourceSession,
      target_device_id: input.targetDeviceId,
      cwd: input.cwd,
      ...(input.newName ? { new_name: input.newName } : {}),
    }),
  })
  const text = await r.text()
  try {
    return JSON.parse(text) as ForkAcrossResult
  } catch {
    // A 200 that is not JSON is an error with its first bytes as evidence — the same
    // rule bffJson follows, and for the same reason it was written.
    return { error: 'bad_response', message: `${r.status} answered non-JSON: ${text.slice(0, 80)}` }
  }
}

/** One leg of the transfer, in the order they happen. */
export type ForkLeg = 'conversation' | 'asking_source' | 'handing_key' | 'creating'
export type ForkStage = ForkLeg | 'done' | 'failed' | 'unknown'

export type ForkProgress = {
  stage?: ForkStage
  elapsed_s?: number
  legs?: { leg: ForkLeg; elapsed_s?: number; finished?: boolean }[]
  error?: string
  message?: string
}

/**
 * Where a fork has got to. Each leg carries its OWN clock, which is why the UI can say
 * "asking <machine> (40s)" instead of drawing a percentage it would be inventing.
 *
 * `unknown` is NOT a failure: it means this instance never ran that fork (another one
 * did, or the record aged out). The two must not render the same, because they send the
 * reader to two different places.
 */
export const forkAcrossProgress = (forkId: string) =>
  bffJson<ForkProgress>(`/account/agents/fork-across/${encodeURIComponent(forkId)}`)

export const unpairDevice = (deviceId: string) =>
  bffJson<{ ok?: boolean; state?: string }>(
    `/account/devices/${encodeURIComponent(deviceId)}/unpair`,
    { method: 'POST' },
  )

/** Only answers while the machine has NOT been told; 409 after — show it verbatim. */
export const cancelUnpair = (deviceId: string) =>
  bffJson<{ ok?: boolean; state?: string }>(
    `/account/devices/${encodeURIComponent(deviceId)}/unpair/cancel`,
    { method: 'POST' },
  )

/**
 * Set or clear the account's own name for a machine (owner, 2026-08-19: Rename
 * 不是真的 Rename 該機器，而是給一個 Nick name). null or empty clears — the row
 * falls back to the hostname. The BFF stores it in DynamoDB keyed by device_id
 * and fans the change out on the account WS devices frames, which is what makes
 * one rename land on every open app at once.
 */
/* ── pinned agents (global, per account) — same BFF/same-origin contract as the
   nickname above: session cookie on credentials:include, Origin gate on the POSTs,
   stored in DynamoDB keyed by (device, hash) so a pin set on one device is there on
   the next. usePins reads getPins and re-reads on the account stream (re)opening. */

/** What the account has pinned and the tags it is filed under, as the BFF sends it.
 *  `tags` is absent from a BFF from before tags (owner, 2026-10-12); a pin names its
 *  `tag` only when that is not the first one. */
export type PinsAnswer = {
  pins: Array<{ device: string; hash: string; session: string; machine: string; rank?: number; tag?: string }>
  tags?: Array<{ id: string; name: string }>
}

/** Every agent this account has pinned. */
export const getPins = () => bffJson<PinsAnswer>('/account/pins')

/**
 * THE TABS AGENTS HOLD in this account's browsers that are connected now — owner,
 * 2026-10-13: More shows which Chrome tabs are given to which agent, and the phone can
 * pause/resume one and mute/unmute its "Notify agent". BFF routes_browser.py. The
 * title is what the tab is called; its address never leaves the browser.
 */
export type HeldTab = {
  tab: string
  title: string
  by: 'user' | 'agent'
  /** The tab's "Notify agent" switch; null from an extension before 0.2.21, which does not say. */
  watch: boolean | null
  paused: boolean
  agent: { device: string; hash: string; session: string; machine: string; online: boolean }
}
export type HeldTabsBrowser = {
  browser_id: string
  name: string
  version: string
  /** The browser's extension takes Pause and Mute from the app (0.2.21+). */
  controls: boolean
  /**
   * …and takes a picture of a held tab for the app (0.2.22+). Absent from a cloud from
   * before pictures, which cannot be asked for one either.
   */
  shots?: boolean
  tabs: HeldTab[]
}
export const getBrowserTabs = () => bffJson<{ browsers: HeldTabsBrowser[] }>('/account/browser/tabs')

/** Pause/resume a held tab, or turn its "Notify agent" on/off. Asked of the browser that holds it. */
export const setBrowserTab = (p: { browser_id: string; tab: string } & ({ paused: boolean } | { watch: boolean })) =>
  bffJson<{ ok: true }>('/account/browser/tab', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(p),
  })

/**
 * A picture of a held tab, as it is on screen now — taken by the browser that holds it,
 * in the tab's own turn (owner, 2026-10-14: More › Chrome tabs › a tab). Relayed, not
 * kept: a fresh one is asked for each time. With `url`, the tab's web address comes too
 * (extension 0.2.23+; an older one answers the picture alone), for opening the page here.
 */
export const getBrowserTabShot = (p: { browser_id: string; tab: string; fit?: number; url?: boolean }) =>
  bffJson<{ ok: true; image: { mime: 'image/jpeg' | 'image/png'; base64: string }; url?: string }>('/account/browser/tab/shot', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(p),
  })

/**
 * Pin one agent UNDER a tag, or move a pinned one there — the whole move, in one
 * write. The tag is named by `tag` (the id of one that exists) or by `tag_name` (one
 * to make, or the one that already has that name): a tag is made BY pinning under
 * it. device is '' on a single-machine account. Answers the pins and tags as they
 * now are, and `tag` when it was named by name.
 *
 * A PATH of its own rather than a `tag` on POST /account/pins: a BFF from before
 * tags would pin it and ignore the field, and the rail would draw it under a
 * heading the server never heard of.
 */
export const placePin = (
  p: { device: string; hash: string; session: string; machine: string } & ({ tag: string } | { tag_name: string }),
) =>
  bffJson<PinsAnswer & { ok?: boolean; tag?: { id: string; name: string } }>('/account/pins/place', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(p),
  })

/** Pin one agent the way an app from before tags did — under the first tag. Kept for
 *  ONE caller: a server that has no /account/pins/place yet (usePins.place). */
export const addPin = (p: { device: string; hash: string; session: string; machine: string }) =>
  bffJson<{ ok?: boolean }>('/account/pins', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(p),
  })

/** Call a tag something else. Refused (`tag_exists`) when another tag has the name. */
export const renamePinTag = (p: { id: string; name: string }) =>
  bffJson<PinsAnswer & { ok?: boolean }>('/account/pins/tags/rename', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(p),
  })

/** Remove a tag. Its agents stay pinned, under the first tag. */
export const removePinTag = (id: string) =>
  bffJson<PinsAnswer & { ok?: boolean }>('/account/pins/tags/remove', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id }),
  })

/** Unpin (device, hash). A no-op server-side when it was not pinned. */
export const removePin = (p: { device: string; hash: string }) =>
  bffJson<{ ok?: boolean }>('/account/pins/remove', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(p),
  })

/** Place one pin in the account's vertical order — one write per drag; the
 *  server sorts by rank so every device draws the same Pinned section. */
export const reorderPin = (p: { device: string; hash: string; rank: number }) =>
  bffJson<{ ok?: boolean }>('/account/pins/reorder', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(p),
  })

/* ── agent backups: the vault and the list — BFF routes_backup.py ──
   Owner, 2026-09-29: 「Backup this agent」, encrypted behind a vault only its owner can
   open, and a Backups section in More. BFF-served, so bffJson (the session cookie; the
   browser adds Origin on the POSTs). The vault travels as the device made it: a public
   key and WRAPPED secrets — the password and the recovery code never leave the device
   (backup/vaultCrypto.ts). The backup itself is the EDGE's job: api.ts agentBackup. */

/** One wrapped copy of the vault key: PBKDF2-SHA256(secret, salt, iter) → AES-GCM. */
export type VaultWrap = { salt: string; iter: number; iv: string; ct: string }

/** The vault as stored: its public key in the clear, everything else wrapped. */
export type PasskeyWrap = { cred: string; salt: string; iv: string; ct: string; at?: number }

export type StoredVault = {
  v?: number
  vpk: string
  enc_vsk: { iv: string; ct: string }
  pw: VaultWrap
  rc: VaultWrap
  /** Face ID / Touch ID doors, one per passkey. */
  pk?: PasskeyWrap[]
}

/** The limits, served by the BFF so a paid tier changes numbers there and nothing here. */
export type BackupPolicy = { keep_per_agent: number; account_bytes: number; part_size: number }

export type BackupRow = {
  backup_id: string
  session: string
  kind: string
  device_id: string
  machine: string
  created_at: number
  committed_at?: number | null
  /** 'uploading' while its machine is still sending it; 'ready' once every part landed. */
  state: string
  packed_size: number
  plain_size: number
  parts: number
  /** Set on a copy a friend shared and this account accepted. */
  shared_by?: { name?: string | null; email?: string | null } | null
}

export const getVault = () => bffJson<{ vault: StoredVault | null; policy: BackupPolicy }>('/account/vault')

/** 409 `vault_exists` when there is one: replacing a vault is resetVault, which deletes every backup. */
export const createVault = (vault: StoredVault) =>
  bffJson<{ ok: boolean }>('/account/vault', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(vault),
  })

/** A new password for the SAME vault key (the recovery-code path) — backups are kept. */
export const setVaultPassword = (pw: VaultWrap) =>
  bffJson<{ ok: boolean }>('/account/vault/password', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pw }),
  })

/** Face ID / Touch ID: one more wrap of the SAME vault key, under a passkey. */
export const addVaultPasskey = (pk: PasskeyWrap) =>
  bffJson<{ ok: boolean }>('/account/vault/passkey', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pk }),
  })

/** Face ID / Touch ID off: every passkey door goes (the password and recovery code stay). */
export const removeVaultPasskeys = () =>
  bffJson<{ ok: boolean }>('/account/vault/passkey/remove', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ all: true }),
  })

/** Every backup, then the vault. The server refuses without this exact confirmation. */
export const resetVault = () =>
  bffJson<{ ok: boolean; deleted: number }>('/account/vault/reset', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ confirm: 'delete-all-backups' }),
  })

export const getBackups = () =>
  bffJson<{ backups: BackupRow[]; used_bytes: number; policy: BackupPolicy }>('/account/backups')

/** A backup's key as sealed to the vault — opened on this device, re-sealed to the restoring machine. */
export const getBackupKey = (backupId: string) =>
  bffJson<{ backup: BackupRow; wrapped_dek: { eph: string; iv: string; ct: string } }>(
    `/account/backups/${encodeURIComponent(backupId)}/key`,
  )

/* ── sharing a backup with a friend — BFF routes_backup_share.py (owner, 2026-09-30) ── */

type Sealed = { eph: string; iv: string; ct: string }

/** A friend as a share target: their opaque account handle, and their vault's public key. */
export type ShareTarget = {
  account: string
  name?: string | null
  email?: string | null
  picture?: string | null
  has_vault: boolean
  vpk: string | null
}
export type IncomingShare = {
  share_id: string
  from_name?: string | null
  from_email?: string | null
  backup: BackupRow
  created_at: number
  expires_at: number
}
export type OutgoingShare = { share_id: string; backup_id: string; session: string; to_name?: string | null; created_at: number }

const postJson = <T,>(path: string, body: unknown) =>
  bffJson<T>(path, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })

export const getShareTargets = () => bffJson<{ friends: ShareTarget[] }>('/account/backups/share-targets')
export const shareBackup = (body: { backup_id: string; account: string; vpk: string; wrapped_dek: Sealed }) =>
  postJson<{ ok: boolean; share_id: string }>('/account/backups/share', body)
export const getShares = () => bffJson<{ incoming: IncomingShare[]; outgoing: OutgoingShare[] }>('/account/backups/shares')
export const acceptShare = (id: string) =>
  postJson<{ ok: boolean; backup: BackupRow }>(`/account/backups/shares/${encodeURIComponent(id)}/accept`, {})
export const declineShare = (id: string) => postJson<{ ok: boolean }>(`/account/backups/shares/${encodeURIComponent(id)}/decline`, {})
export const cancelShare = (id: string) => postJson<{ ok: boolean }>(`/account/backups/shares/${encodeURIComponent(id)}/cancel`, {})

/* ── a LIVE agent shared with a friend (owner, 2026-10-17) — BFF routes_agent_share.py ──
   The friend never reaches the machine: every friend call names a SHARE, and the cloud
   builds the one request it allows for the one agent it names (and re-checks the share,
   the friendship and the expiry each time). */

export type AgentShareStatus = 'pending' | 'accepted' | 'expired'
export type AgentShareIdentity = 'friend' | 'owner'
export type AgentShareTerms = {
  identity: AgentShareIdentity
  /** The conversation from before the friend accepted. Off unless chosen. */
  history: boolean
  /** The files in the agent's working folder. Off unless chosen. */
  files: boolean
  /**
   * The keys row (⌨: Tab, ⇧Tab, /, the arrows), straight into the agent's terminal. Off unless
   * chosen — owner, 2026-10-17: 小鍵盤 B. Absent from an older cloud: off.
   */
  keys?: boolean
  /** Pictures of the Chrome tabs the agent holds (owner, 2026-10-17). Off unless chosen. */
  tabs?: boolean
  created_at: number
  accepted_at: number | null
  /** Epoch seconds; null when shared until taken back. */
  expires_at: number | null
}
/**
 * A LINK (phase 2, owner 2026-10-17: 跨帳號的 @): one of the friend's own agents, which the friend
 * @-pinged, paired with the shared one. `address` is the fr_… address the reader's side uses — the
 * friend's agent's way to the shared one (incoming), or the shared agent's way to it (outgoing).
 * The friend's view also names its own agent (device_id, hash).
 */
/** One of the friend's agents a share reaches. Its machine, device and hash are said to the friend only. */
export type SharedLink = { label: string; address: string; machine?: string; device_id?: string; hash?: string }
export type OutgoingAgentShare = AgentShareTerms & {
  links?: SharedLink[]
  share_id: string
  status: AgentShareStatus
  friend: { name?: string | null; email?: string | null }
  /** The friend's opaque account handle — never their address (multi-account). */
  account: string
  device_id?: string
  session?: string
  hash?: string
}
export type IncomingAgentShare = AgentShareTerms & {
  links?: SharedLink[]
  /** The friend's own switch: their phone is told when it finishes (owner, 2026-10-17). */
  notify?: boolean
  share_id: string
  status: AgentShareStatus
  from: { name?: string | null; email?: string | null }
  from_account?: string
  agent: { label: string; session: string; cli: string | null; machine: string; hash?: string | null }
}
export type AgentShareTarget = { account: string; name?: string | null; email?: string | null; picture?: string | null }
export type SharedTranscript = {
  ok: boolean
  file: string
  mode: 'replace' | 'append' | 'backfill'
  from: number
  total: number
  truncated: boolean
  blocks: TranscriptBlock[]
  /** Older blocks the friend may still load. */
  pending: number
  /** Some of this page came from before the share and was held back. */
  history_hidden: boolean
}
export type SharedStatus = { ok: boolean; status: 'active' | 'idle' | 'unknown' | 'off' | string; needs_input?: boolean; reachable: boolean }

const shareUrl = (id: string, tail = '') => `/account/agent-shares/${encodeURIComponent(id)}${tail}`

export const getAgentShares = () =>
  bffJson<{ outgoing: OutgoingAgentShare[]; incoming: IncomingAgentShare[] }>('/account/agent-shares')
export const getAgentShareTargets = () => bffJson<{ friends: AgentShareTarget[] }>('/account/agent-shares/targets')
export const shareAgent = (body: {
  device_id: string
  session: string
  accounts: string[]
  duration: '1d' | 'forever'
  identity: AgentShareIdentity
  history: boolean
  files: boolean
  keys: boolean
  tabs: boolean
}) => postJson<{ ok: boolean; shares: OutgoingAgentShare[] }>('/account/agent-shares', body)
export const revokeAgentShare = (id: string) => postJson<{ ok: boolean }>(shareUrl(id, '/revoke'), {})
/** The agent was ended: every share of it goes, and each friend is told (a later agent given its
 *  name is a different agent — review of 2026-10-17). */
export const forgetAgentShares = (device_id: string, session: string) =>
  postJson<{ ok: boolean; revoked: number }>('/account/agent-shares/forget', { device_id, session })
export const acceptAgentShare = (id: string) =>
  postJson<{ ok: boolean; share: IncomingAgentShare }>(shareUrl(id, '/accept'), {})
export const declineAgentShare = (id: string) => postJson<{ ok: boolean }>(shareUrl(id, '/decline'), {})
export function getSharedTranscript(
  id: string,
  q: { file?: string; have?: number; tail?: number; before?: number },
): Promise<SharedTranscript> {
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(q)) if (v !== undefined) p.set(k, String(v))
  return bffJson<SharedTranscript>(shareUrl(id, `/transcript?${p.toString()}`))
}
/** `files`: what uploadSharedFile handed back — the cloud knows where each one landed; the friend never does. */
export const saySharedAgent = (
  id: string,
  text: string,
  files: string[] = [],
  /** The reader's own agents @-pinged in `text` — the cloud proves each theirs and links it. */
  pings: { hash: string; device_id: string }[] = [],
) =>
  postJson<{ ok: boolean; delivered: boolean; queued: boolean }>(shareUrl(id, '/say'), {
    text,
    ...(files.length ? { files } : {}),
    ...(pings.length ? { pings } : {}),
  })
/** From one of the reader's OWN agents' sessions: link it with this shared agent; the address it uses. */
export const linkSharedAgent = (id: string, mine: { hash: string; device_id: string }) =>
  postJson<{ ok: boolean; address: string; label: string; owner: string }>(shareUrl(id, '/link'), mine)
/** The Chrome tabs the shared agent holds, when the share shows them — a key and a title each. */
export const getSharedTabs = (id: string) =>
  bffJson<{ ok: boolean; tabs: { key: string; title: string; shots: boolean }[] }>(shareUrl(id, '/tabs'))
/** A picture of one of them (never the page's address). */
export const getSharedTabShot = (id: string, key: string, fit: number) =>
  postJson<{ ok: boolean; image: { mime: 'image/jpeg' | 'image/png'; base64: string } }>(shareUrl(id, '/tab_shot'), { key, fit })
/** The friend's switch: tell my phone when this shared agent finishes. */
export const notifySharedAgent = (id: string, on: boolean) =>
  postJson<{ ok: boolean; notify: boolean }>(shareUrl(id, '/notify'), { on })
/** Either side: that pair of agents stops reaching each other (the share stays). */
export const unlinkSharedAgent = (id: string, address: string) =>
  postJson<{ ok: boolean }>(shareUrl(id, '/unlink'), { address })
/** A key of the keys row, by NAME — the machine maps it; no byte a friend chooses reaches the pane. */
export type SharedKey = 'tab' | 'shift_tab' | 'slash' | 'up' | 'down' | 'left' | 'right'
/** One key of the keys row, for a friend the sharer gave the keys. */
export const pressSharedKey = (id: string, key: SharedKey) => postJson<{ ok: boolean }>(shareUrl(id, '/key'), { key })
/** The friend's Stop: the machine sends the owner's own key for the agent's kind, and only while it works. */
export const stopSharedAgent = (id: string) =>
  postJson<{ ok: boolean; sent: boolean; why: string }>(shareUrl(id, '/stop'), {})
/** A file a friend attached, as the cloud hands it back — never where it landed on their machine. */
export type SharedUpload = { ok: boolean; file_id: string; name: string; size: number | null; mime: string }
/** The cloud's cap for a file sent to a friend's agent (routes_agent_share.FRIEND_FILE_MAX). */
const SHARED_FILE_MAX = 25 * 1024 * 1024
/**
 * The paperclip, for a friend's agent: the owner's own upload lane (the edge's 512 KiB slices),
 * each step asked of their machine by the cloud for this share. Returns the id the next say carries.
 */
export async function uploadSharedFile(
  id: string,
  file: File,
  onProgress?: (pct: number) => void,
): Promise<SharedUpload> {
  if (file.size > SHARED_FILE_MAX) throw new Error(`${file.name} is larger than the 25 MB a friend's agent takes`)
  if (file.size === 0) throw new Error(`${file.name} is empty`)
  const begin = await postJson<{ ok: boolean; upload_id: string }>(shareUrl(id, '/upload_begin'), {
    name: file.name,
    mime: file.type || 'application/octet-stream',
    size: file.size,
  })
  let seq = 0
  for (let o = 0; o < file.size; o += UPLOAD_CHUNK) {
    const buf = await file.slice(o, o + UPLOAD_CHUNK).arrayBuffer()
    await postJson(shareUrl(id, '/upload_chunk'), { upload_id: begin.upload_id, seq, data_b64: b64(buf) })
    seq += 1
    onProgress?.(Math.round(Math.min(100, ((o + buf.byteLength) / Math.max(1, file.size)) * 100)))
  }
  return postJson<SharedUpload>(shareUrl(id, '/upload_finish'), { upload_id: begin.upload_id })
}
export const getSharedStatus = (id: string) => bffJson<SharedStatus>(shareUrl(id, '/status'))
export type SharedFolder = {
  ok: boolean
  /** The folder's NAME on their machine — never its path. */
  folder: string
  rel: string
  entries: { name: string; type: 'dir' | 'file'; size?: number | null; kind?: string }[]
  truncated: boolean
}
export type SharedFile = {
  ok: boolean
  name?: string
  size?: number
  kind?: string
  mime?: string
  encoding?: 'utf8' | 'base64'
  data?: string
  truncated?: boolean
  /** `too_large` for a picture or a binary past the limit (a text file truncates instead). */
  error?: string
  limit?: number
}
/* POSTs that only READ, on purpose: the path rides in the body, because a URL is written to the
   box's access log and the names in someone's folder do not belong there (routes_agent_share). */
export const getSharedFiles = (id: string, rel: string) => postJson<SharedFolder>(shareUrl(id, '/files'), { rel })
export const getSharedFile = (id: string, rel: string) => postJson<SharedFile>(shareUrl(id, '/file'), { rel })

/** The BFF's own sentence for a refusal (`message`), else what bffJson threw. */
export function shareErrText(e: unknown): string {
  if (e instanceof BffError && typeof e.body.message === 'string' && e.body.message) return e.body.message
  return e instanceof Error ? e.message : String(e)
}

export const deleteBackup = (backup_id: string) =>
  bffJson<{ ok: boolean }>('/account/backups/delete', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ backup_id }),
  })

export const setDeviceNickname = (deviceId: string, nickname: string | null) =>
  bffJson<{ ok?: boolean; nickname?: string | null }>(
    `/account/devices/${encodeURIComponent(deviceId)}/nickname`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ nickname }),
    },
  )

/* ── friends (person ↔ person) — avg-main's BFF contract, /account/friends/* ──
   BFF-served like the machines list above, so they go DIRECT via bffJson (session
   cookie on credentials:include; the browser adds Origin on the POSTs, which is the
   same-origin gate the backend checks). NOT api.get/api.post — those relay to a paired
   EDGE, which has no such route and answers `not_found` (the bug the owner hit). And
   living here keeps them under this file's csrfCoverage exemption: the edge's CSRF
   token has no meaning at the BFF, same as every other /account route above. */

/** The one relationship the search returns — it decides which single button to draw. */
export type FriendRelationship = 'none' | 'outgoing' | 'incoming' | 'friends' | 'self' | 'blocked'
/** A found account, by its public handle only. */
export type FriendAccount = {
  email: string
  name?: string | null
  picture?: string | null
  /** The key friends seal to — the account's PUBLISHED identity. On the wire
   *  since E2E shipped; the type just never said so. */
  public_key?: string | null
  /** WHICH DOOR THIS ACCOUNT CAME IN BY — owner, 2026-09-14: 搜尋到的 Account 上面要
   *  註明這個是 Google 的 Account, 那個是 Apple 的 Account. The only thing that tells
   *  two accounts at one address apart on screen. */
  provider?: 'google' | 'apple'
  /** The handle that says WHICH of them an action means. A hash of the identity —
   *  never `sub` — and only resolvable against the accounts at that address. */
  account?: string
  /** Per-account, because the viewer can be friends with one of them and not the
   *  other. The top-level `relationship` is the first one's, for old clients. */
  relationship?: FriendRelationship
}
/** A confirmed friend, as the Group-column list reads them. `public_key` is their
 *  account encryption key (absent until they set up DMs) — it rides the friends list
 *  so sealing a message needs no extra round-trip. */
export type Friend = {
  email: string
  name?: string | null
  since?: number
  public_key?: string | null
  picture?: string | null
  /** Which account at the address (bff account_id) — opens their profile page when the address is two accounts. */
  account?: string
}
/** A pending request either direction, for the request tray. */
export type FriendRequest = { email: string; name?: string | null; created_at?: number }

/**
 * `account` is optional and usually absent — owner, 2026-09-14: 我同一個 email 同時
 * 註冊了 Google Account 以及 Apple Account.
 *
 * One address can be two accounts, and the server refuses to guess between them
 * (it would send somebody's friend request to the wrong person and look like it
 * worked). The handle a search returned says which one is meant. Left out when the
 * address is one person, which is nearly always.
 */
const friendPost = <T>(path: string, email: string, account?: string) =>
  bffJson<T>(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(account ? { email, account } : { email }),
  })

/**
 * Look someone up by email. `found:false` is a real, non-error answer (nobody by that
 * address has ever signed in); `relationship` on a hit is what the UI turns into
 * exactly one button.
 */
export const friendSearch = (email: string) =>
  friendPost<{
    found: boolean
    /** EVERY account at the address, each labelled with the door it came in by. */
    accounts?: FriendAccount[]
    /** The first of them. The pre-2026-09-14 shape, still sent, still right when the
     *  address is one person — which is the only case it was ever right for. */
    account?: FriendAccount
    relationship?: FriendRelationship
  }>('/account/friends/search', email)

/**
 * Ask to be friends. `status:"accepted"` (not "pending") when the other side had
 * already requested you — the two requests auto-complete into a friendship, and the UI
 * must not then show "請求中".
 */
export const friendRequest = (email: string, account?: string) =>
  friendPost<{ ok: boolean; status: 'pending' | 'accepted' }>('/account/friends/request', email, account)

export const friendAccept = (email: string, account?: string) =>
  friendPost<{ ok: boolean }>('/account/friends/accept', email, account)

export const friendReject = (email: string, account?: string) =>
  friendPost<{ ok: boolean }>('/account/friends/reject', email, account)

/** Withdraw a request I sent. */
export const friendCancel = (email: string, account?: string) =>
  friendPost<{ ok: boolean }>('/account/friends/cancel', email, account)

/**
 * INVITING A FRIEND TO AVENGENTS, and the SPARKS it earns — owner, 2026-10-19: Invite friends 有兩種形式, 一種是
 * 使用邀請連結, 一種是使用寄出 email 的方式 … 每邀請到一個好朋友可以累積十個 Spark (BFF routes_invite.py).
 * A refusal's own sentence rides in `BffError.body.message`.
 */
export const sendInvite = (email: string, message: string) =>
  bffJson<{ ok: boolean }>('/account/invite', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, message }),
  })

/** This person's own invite link — the same every time. */
export const getInviteLink = () => bffJson<{ url: string }>('/account/invite/link')

/** Who a link is from, for the sign-in screen it opens on (no session needed). */
export const inviteFrom = (code: string) =>
  bffJson<{ name: string }>(`/account/invite/from?code=${encodeURIComponent(code)}`)

/** Signed in after opening someone's link: their Sparks if this account is new (the server decides). Nothing is sent to anyone. */
export const acceptInvite = (code: string) =>
  bffJson<{ ok: boolean; name: string; self?: boolean }>('/account/invite/accept', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  })

/** From the welcome sheet: ask the link's owner to be friends — the newcomer's choice; the owner decides. */
export const askInviter = (code: string) =>
  bffJson<{ ok: boolean }>('/account/invite/befriend', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  })

/** A new link; the old one stops working. */
export const resetInviteLink = () =>
  bffJson<{ url: string }>('/account/invite/link/reset', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
  })

export type SparksView = {
  balance: number
  recent: { reason: 'invite' | 'win' | string; amount: number; at: number; name?: string }[]
}
export const getSparks = () => bffJson<SparksView>('/account/sparks')

/**
 * BLOCK — owner, 2026-10-17 (App Store guideline 1.2). The friendship, every request and every agent
 * shared either way end; they can no longer ask, share or message; they are not told.
 */
export type BlockedAccount = { email: string; name?: string | null; picture?: string | null; at: number; account: string }
export const blockAccount = (email: string, account?: string) =>
  friendPost<{ ok: boolean }>('/account/blocks', email, account)
export const getBlocked = () => bffJson<{ blocked: BlockedAccount[] }>('/account/blocks')
/** Unblock by the handle the list (or a search) gave. Nothing comes back by itself. */
export const unblockAccount = (account: string) =>
  bffJson<{ ok: boolean }>('/account/blocks/remove', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ account }),
  })

/* ── groups (a person ↔ people) — /account/groups/* (owner, 2026-10-18) ─────────────
   BFF-served like the friends routes above, so DIRECT via bffJson. 加入群組並不等於加入好友:
   a group is its own relation — nothing here makes anyone a friend. A person in a group is
   named back to the server by `member`, an opaque per-group handle, never by an address. */
export type GroupVisibility = 'hidden' | 'searchable'
export type GroupRole = 'owner' | 'member'
/** A group I am in, for the menu's Groups card. `asks` = requests to join waiting on me (owner only). */
export type GroupRow = { gid: string; name: string; role: GroupRole; visibility: GroupVisibility; members: number; asks: number; icon?: string | null }
/** An invitation waiting on me. `by` = the inviter's name, when they set one. */
export type GroupInvitation = { gid: string; name: string; at: number; by?: string | null; icon?: string | null }
/** A person in (or invited to, or asking into) a group. `email` only where the viewer already has it. */
export type GroupPerson = {
  member: string
  name?: string | null
  picture?: string | null
  email?: string | null
  role?: GroupRole
  you?: boolean
  friend?: boolean
  /** The viewer's dm with this member, by conversation id (absent on yourself) — for its unread count. */
  dm?: string
  /** Me to them (friends.relationship) — what the row's Add friend shows. Absent on invites and asks. */
  relationship?: 'self' | 'friends' | 'outgoing' | 'incoming' | 'none'
  joined_at?: number
  at?: number
}
/** One group as a member sees it; `invites` and `asks` only for its owner. */
export type GroupView = {
  gid: string
  name: string
  visibility: GroupVisibility
  role: GroupRole
  members: GroupPerson[]
  invites?: GroupPerson[]
  asks?: GroupPerson[]
  /** The group's link code — every member may pass `/?group=<link>` on (groups/groupLink.ts). */
  link?: string
  /** The owner's social links (Facebook, X, YouTube…), `site` = the name to show. */
  social?: { url: string; site: string }[]
  /** The owner's picture for the group, a same-origin /account/avatar/<id> URL. */
  icon?: string | null
  /** The owner's rules for the group, in Markdown ('' = none); every member reads them. */
  rules?: string
  /** When the rules last changed (epoch seconds), or null when there are none. */
  rules_at?: number | null
  /** The owner's limits (owner, 2026-10-20): posts per member a day (shared with their agents), agents per
   *  member; null = no limit. Absent from a server older than them. */
  limits?: GroupLimits
}
export type GroupLimits = { posts: number | null; agents: number | null }
/** What a group's link shows before joining. */
export type GroupLinkPreview = { gid: string; name: string; members: number; owner?: string | null; status: GroupFound['status']; icon?: string | null }
/** A searchable group, and where the viewer stands with it. */
export type GroupFound = { gid: string; name: string; members: number; status: 'none' | 'asked' | 'invited' | 'member'; icon?: string | null }

const groupPost = <T>(path: string, body: Record<string, unknown> = {}) =>
  bffJson<T>(path, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
const groupPath = (gid: string, verb = '') => `/account/groups/${encodeURIComponent(gid)}${verb ? `/${verb}` : ''}`

export const getGroups = () => bffJson<{ groups: GroupRow[]; invitations: GroupInvitation[] }>('/account/groups')
export const createGroup = (name: string, visibility: GroupVisibility) =>
  groupPost<{ ok: boolean; gid: string }>('/account/groups', { name, visibility })
export const searchGroups = (query: string) => groupPost<{ groups: GroupFound[] }>('/account/groups/search', { query })
export const getGroup = (gid: string) => bffJson<GroupView>(groupPath(gid))
export const updateGroup = (
  gid: string,
  change: {
    name?: string
    visibility?: GroupVisibility
    links?: string[]
    rules?: string
    /** null = no limit. */
    post_limit?: number | null
    agent_limit?: number | null
  },
) =>
  groupPost<{ ok: boolean }>(groupPath(gid, 'update'), change)
/** One Spark for more posts in the group's chat (owner, 2026-10-20: 每一個 Spark 可以增加發文二十次). */
export const buyGroupPosts = (gid: string) =>
  groupPost<{ ok: boolean; added: number; extra: number; sparks: number }>(groupPath(gid, 'more-posts'))
/** `account` — the handle a search returned, when one address is two accounts (friendSearch). */
export const inviteToGroup = (gid: string, email: string, account?: string) =>
  groupPost<{ ok: boolean; status: 'invited' | 'joined' }>(groupPath(gid, 'invite'), account ? { email, account } : { email })
export const cancelGroupInvite = (gid: string, member: string) =>
  groupPost<{ ok: boolean }>(groupPath(gid, 'invite/cancel'), { member })
export const acceptGroup = (gid: string) => groupPost<{ ok: boolean }>(groupPath(gid, 'accept'))
export const declineGroup = (gid: string) => groupPost<{ ok: boolean }>(groupPath(gid, 'decline'))
export const askToJoinGroup = (gid: string) =>
  groupPost<{ ok: boolean; status: 'asked' | 'joined' }>(groupPath(gid, 'ask'))
export const withdrawGroupAsk = (gid: string) => groupPost<{ ok: boolean }>(groupPath(gid, 'ask/withdraw'))
export const answerGroupAsk = (gid: string, member: string, letIn: boolean) =>
  groupPost<{ ok: boolean }>(groupPath(gid, 'asks/answer'), { member, let_in: letIn })
export const removeFromGroup = (gid: string, member: string) =>
  groupPost<{ ok: boolean }>(groupPath(gid, 'remove'), { member })
/** Add friend, from the member list — a friend request by the member's handle (accepts theirs if they asked). */
export const befriendInGroup = (gid: string, member: string) =>
  groupPost<{ ok: boolean; status: 'pending' | 'accepted' }>(groupPath(gid, 'befriend'), { member })
export const cancelBefriendInGroup = (gid: string, member: string) =>
  groupPost<{ ok: boolean }>(groupPath(gid, 'befriend/cancel'), { member })
/** The owner's picture for the group (bytes from auth/profileImage avatarFromFile), or null to clear it. */
export const setGroupIcon = (gid: string, avatar: { data: string; mime: string } | null) =>
  groupPost<{ ok: boolean; icon: string | null }>(groupPath(gid, 'icon'), { avatar })
/** The link (owner, 2026-10-18): preview it, join by it, and — the owner — replace it. */
export const getGroupLink = (code: string) => bffJson<GroupLinkPreview>(`/account/groups/link/${encodeURIComponent(code)}`)
export const joinGroupLink = (code: string) =>
  groupPost<{ ok: boolean; status: 'joined' | 'member'; gid: string }>(`/account/groups/link/${encodeURIComponent(code)}/join`)
export const resetGroupLink = (gid: string) => groupPost<{ ok: boolean; link: string }>(groupPath(gid, 'link/reset'))
/** Leave. An owner may name the next owner (a member handle); without one the earliest-joined takes it. */
export const leaveGroup = (gid: string, nextOwner?: string) =>
  groupPost<{ ok: boolean; new_owner?: boolean }>(groupPath(gid, 'leave'), nextOwner ? { next_owner: nextOwner } : {})
/** The group's chat (owner, 2026-10-18): the BFF brings the room along to the group and answers its id. */
export const openGroupChat = (gid: string) => groupPost<{ ok: boolean; conv_id: string }>(groupPath(gid, 'chat'))
/** A dm with a member, by their handle — never by an address the app may not have (only friends' are shown). */
export const openGroupDm = (gid: string, member: string) =>
  groupPost<{ ok: boolean; conv_id: string }>(groupPath(gid, 'dm'), { member })

/** Unfriend — one-sided and silent: the BFF dissolves the mutual edge, no notify. `account` says which
 *  friend when the address is two of them (the BFF refuses `ambiguous_account` otherwise). */
export const friendRemove = (email: string, account?: string) =>
  friendPost<{ ok: boolean }>('/account/friends/remove', email, account)

/** The Group column reads this. */
export const getFriends = () => bffJson<{ friends: Friend[] }>('/account/friends')

/**
 * One friend as the ARTIFACT AUDIENCE PICKER needs them — owner, 2026-09-20:
 * 「彈跳出來一個選單讓使用者可以選擇要把哪些朋友加進去」.
 *
 * `id` is the part that matters and the reason this is not just `getFriends`.
 * A chosen-friends list is stored on the publishing machine and sent to a relay
 * that holds no identities at all, so what gets written down cannot be an
 * address — it is `sha256("iss|sub")[:32]`, the same value the viewer token
 * carries, which is exactly what the relay compares against. It is also why an
 * address could not do the job even if we were willing: one email can be two
 * accounts at two providers, and a tick has to mean one person.
 */
export type ArtifactAudience = {
  id: string
  email: string
  name?: string | null
  picture?: string | null
}

/** BFF-served, so bffJson — the edge has no such route and would answer not_found. */
export const getArtifactAudience = () =>
  bffJson<{ friends: ArtifactAudience[] }>('/account/artifacts/audience')

/**
 * One artifact a FRIEND is sharing with this account right now — More › Artifacts ›
 * Shared with me (owner, 2026-10-05).
 *
 * `title` is what the list shows (the artifact's summary, else its name — the same choice
 * the owner's own list makes); `shared_by` is the friend as THIS account knows them.
 * Served by the BFF, which asks the artifact relay what this viewer may open and names
 * the owners: no machine of this account is involved, so none is asked.
 */
export type SharedArtifact = {
  hash: string
  /**
   * The SHARE LINK — `<app host>/artifacts/<hash>/`, in every mode. It is what the row's
   * `href` and the viewer's "Open in your browser" carry, and it opens in any browser
   * after signing in. It is never what the app frames.
   */
  url: string
  /**
   * The way in for a window that does NOT hold this account's session — a viewer ticket
   * for this one artifact, addressed to the relay (artifacts/artifactWindow.ts). Absent
   * from a cloud that predates it, and from one whose artifacts have a host of their own:
   * there the ticket is `frame_url` instead.
   */
  open_url?: string
  /**
   * What the in-app viewer FRAMES — owner, 2026-10-05: a friend's shared artifact opens in
   * the App's main pane. The same kind of ticket as `open_url`, addressed to the artifacts'
   * OWN host. Sent only by a cloud that has such a host; whether it is framed is decided by
   * artifacts/everywhere.ts `sharedViewGate`, never by its mere presence.
   */
  frame_url?: string
  title: string
  name: string
  summary: string
  kind: 'static' | 'port'
  since: number | null
  shared_by: { id: string; email: string; name?: string | null; picture?: string | null }
}

export const getSharedArtifacts = () =>
  bffJson<{ artifacts: SharedArtifact[] }>('/account/artifacts/shared')

/**
 * WHERE ARTIFACT PAGES ARE SERVED — owner, 2026-10-05: artifacts move to a hostname of
 * their own, so the browser itself keeps every artifact page apart from the App.
 *
 * `content_base` is `<content host>/artifacts` where the cloud has such a host, and null
 * where artifacts are still served on the App's own. ASKED, NOT BAKED: a value built into
 * the bundle follows the bundle from dev to prod (avengents-homepage's
 * tools/promote-to-prod.sh records that hazard for the chat coordinates), while the
 * same-origin cloud always knows its own answer.
 *
 * A cloud that predates the route answers 404 — artifacts/contentHost.ts reads every
 * failure as "not known", which is the behaviour from before there was such a host.
 */
export const getArtifactsWhere = () =>
  bffJson<{ content_base?: string | null }>('/account/artifacts/where')

/**
 * A ticket for one of THIS ACCOUNT'S OWN artifacts on the artifacts' host: `frame_url` is
 * what the in-app viewer frames instead of the share link. The cloud does not look up who
 * owns the hash — the ticket names the caller as owner, and the relay honours it only for
 * an artifact that caller really publishes. 409 `no_content_origin` where artifacts have no
 * host of their own.
 */
export const requestArtifactTicket = (hash: string) =>
  postJson<{ frame_url?: string }>('/account/artifacts/ticket', { hash })

/** The request tray: what I have been sent, and what I am still waiting on. */
export const getFriendRequests = () =>
  bffJson<{ incoming: FriendRequest[]; outgoing: FriendRequest[] }>('/account/friends/requests')
